import * as React from 'react';
import { useCallback, useState, useMemo } from 'react';
import { useAppDispatch } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import DocumentModal from '@components/document/DocumentModal';
import { ProjectDocumentsModal } from '@pages/projects/components/ProjectDocumentsModal';
import { DataTable } from './components/DataTable';
import { FilterBar } from './components/FilterBar';
import { FileText, Image, File, FileSpreadsheet, Edit } from 'lucide-react';
import { Modal } from '@components/molecules/Modal';
import ReactSelect from '@components/atoms/ReactSelect';
import { DOCUMENT_CATEGORIES_OPTIONS } from '@pages/projects/constants';
import { FormGroup } from '@components/molecules/form/FormGroup';
import {
  useProjects,
  useCreateDocument,
  useDeleteDocument,
  useDocuments,
  useEditDocument,
} from '@hooks/api';

interface DocumentsTableProps {
  projectId?: string;
  showActions?: boolean;
  canEdit?: boolean;
  isReadOnly?: boolean;
}

export const DocumentsTable: React.FC<DocumentsTableProps> = ({
  projectId,
  showActions,
  canEdit = false,
  isReadOnly = false,
}) => {
  const dispatch = useAppDispatch();
  const localeCommon = useTranslation('common');
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    projectId || null,
  );
  const { data: projects } = useProjects();
  const createDocumentMutation = useCreateDocument();
  const deleteDocumentMutation = useDeleteDocument();
  const editDocumentMutation = useEditDocument();
  const [selectedDocuments, setSelectedDocuments] = useState<any[]>([]);
  const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
  const [initialData, setInitialData] = useState<any>([]);
  const [filterText, setFilterText] = useState<string>('');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [showManageDocumentsModal, setShowManageDocumentsModal] =
    useState<boolean>(false);
  const [category, setCategory] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [documentToEdit, setDocumentToEdit] = useState<any>(null);

  // Prepare query parameters for useDocuments hook
  const documentsQueryParams = useMemo(() => {
    const taskIds =
      selectedDocuments.length > 0
        ? selectedDocuments.map((doc) => doc.taskId)
        : [];

    return {
      projectIds: selectedProjectId
        ? [selectedProjectId]
        : projects
            ?.map((project) => project.projectId)
            .filter((id): id is string => Boolean(id)),
      taskIds: taskIds.length > 0 ? taskIds : undefined,
      name: filterText || undefined,
      category: filterCategory || undefined,
      page: currentPage,
      limit: pageSize,
    };
  }, [
    selectedProjectId,
    projects,
    selectedDocuments,
    filterText,
    filterCategory,
    currentPage,
    pageSize,
  ]);

  // Use the documents hook
  const {
    data: documentsData,
    isLoading: loading,
    error,
  } = useDocuments(documentsQueryParams);

  // Extract documents and pagination from the response
  const documents = documentsData?.documents || [];
  const pagination = documentsData?.pagination || null;

  // Handle errors
  React.useEffect(() => {
    if (error) {
      dispatch(
        appendActionMessage({
          message:
            (error as any)?.response?.errors[0]?.message ||
            (error as any)?.message ||
            localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    }
  }, [error, dispatch, localeCommon]);

  React.useEffect(() => {
    if (selectedProjectId) {
      setSelectedDocuments([]);
      setCurrentPage(1);
    } else {
      setSelectedDocuments([]);
    }
  }, [selectedProjectId, filterText, projects]);

  const handleDetail = (doc: any) => {
    setShowDetailModal(true);
    setInitialData(doc);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleClearFilters = useCallback(() => {
    setSelectedProjectId(null);

    setSelectedDocuments([]);
    setFilterText('');
    setFilterCategory('');
    setCurrentPage(1);
  }, []);

  React.useEffect(() => {
    if (projectId) {
      setSelectedProjectId(projectId);
    }
  }, [projectId, selectedProjectId]);

  const handleOpenManageDocuments = () => {
    setShowManageDocumentsModal(true);
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    if (files.length > 0 && selectedProjectId && category) {
      try {
        await createDocumentMutation.mutateAsync({
          files,
          projectId: selectedProjectId,
          category,
        });
        // Documents will be refetched automatically by React Query
        // Success/error messages handled by the mutation hook
      } catch (error: any) {
        // Error handling is done by the mutation hook
        console.error('Document upload error:', error);
      }
    } else {
      dispatch(
        appendActionMessage({
          message:
            'Please select a project and category before uploading files',
          type: SnackType.WARNING,
        }),
      );
    }
  };

  const handleDownload = (pdfUrl: string) => {
    window.open(pdfUrl, '_blank');
  };

  const handleDeleteDocument = async (documentId: string) => {
    try {
      await deleteDocumentMutation.mutateAsync({ documentId });
      // Documents will be refetched automatically by React Query
      // Success/error messages handled by the mutation hook
    } catch (error: any) {
      // Error handling is done by the mutation hook
      console.error('Document deletion error:', error);
    }
  };

  const handleEditDocument = (document: any) => {
    setDocumentToEdit(document);
    setShowEditModal(true);
  };

  const handleUpdateDocument = async (
    documentId: string,
    newCategory: string,
  ) => {
    try {
      // Find the document to get its taskId
      const document = documents.find(
        (doc: any) => doc.documentId === documentId,
      );
      const taskId = document?.taskId;

      await editDocumentMutation.mutateAsync({
        documentId,
        category: newCategory,
        ...(taskId && { taskId }), // Include taskId if it exists
      });
      setShowEditModal(false);
      setDocumentToEdit(null);
      // Documents will be refetched automatically by React Query
      // Success/error messages handled by the mutation hook
    } catch (error: any) {
      // Error handling is done by the mutation hook
      console.error('Document update error:', error);
    }
  };

  const getFileIcon = (filename: string) => {
    if (!filename)
      return (
        <File className="h-9 w-9 text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-md px-1 py-2" />
      );

    const extension = filename.toLowerCase().split('.').pop();
    switch (extension) {
      case 'pdf':
        return (
          <div className="flex items-center justify-center w-8">
            <File className="h-8 w-8 text-red-500 bg-red-100 dark:bg-red-900/20 rounded-md px-1 py-2" />
          </div>
        );
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'svg':
        return (
          <div className="flex items-center justify-center w-8">
            <Image className="h-8 w-8 text-green-500 bg-green-100 dark:bg-green-900/20 rounded-md px-1 py-2" />
          </div>
        );
      case 'doc':
      case 'docx':
      case 'txt':
        return (
          <div className="flex items-center justify-center w-8">
            <FileText className="h-8 w-8 text-blue-500 bg-blue-100 dark:bg-blue-900/20 rounded-md px-1 py-2" />
          </div>
        );
      case 'xls':
      case 'xlsx':
        return (
          <div className="flex items-center justify-center w-8">
            <FileSpreadsheet className="h-8 w-8 text-yellow-500 bg-yellow-100 dark:bg-yellow-900/20 rounded-md px-1 py-2" />
          </div>
        );
      default:
        return (
          <div className="flex items-center justify-center w-8">
            <File className="h-8 w-8 text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-md px-1 py-2" />
          </div>
        );
    }
  };

  const getFileType = (filename: string): string => {
    if (!filename) return 'Unknown';
    const extension = filename.toLowerCase().split('.').pop();
    switch (extension) {
      case 'pdf':
        return 'PDF';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'svg':
        return 'Image';
      case 'doc':
      case 'docx':
        return 'Document';
      case 'xls':
      case 'xlsx':
        return 'Spreadsheet';
      default:
        return 'File';
    }
  };

  const showActionsColumn = canEdit && !isReadOnly;

  const columns = [
    {
      key: 'name',
      label: 'Document Name',
      className: showActionsColumn ? 'col-span-4' : 'col-span-5',
      render: (value: string, row: any) => (
        <div className="flex items-center space-x-3">
          {getFileIcon(value)}
          <div>
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate sm:w-[100px] md:w-[100px] lg:w-[150px] xl:w-[250px]">
              {value || 'Unnamed Document'}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {row.category || 'No category'}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'task',
      label: 'Task',
      className: 'col-span-3',
      render: (value: any, row: any) => (
        <div className="text-sm text-gray-900 dark:text-gray-100">
          {row.task?.name || 'No assigned task'}
        </div>
      ),
    },
    {
      key: 'project',
      label: 'Project',
      className: 'col-span-3',
      render: (value: any, row: any) => {
        const project = row.project;
        return (
          <div className="text-sm text-gray-900 dark:text-gray-100">
            {project?.name || 'Project'}
          </div>
        );
      },
    },
    {
      key: 'type',
      label: 'Type',
      className: 'col-span-2',
      render: (value: any, row: any) => {
        const fileType = getFileType(row.name);
        const getTypeColor = (type: string) => {
          switch (type) {
            case 'PDF':
              return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
            case 'Image':
              return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
            case 'Document':
              return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
            case 'Spreadsheet':
              return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
            default:
              return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
          }
        };

        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(fileType)}`}>
            {fileType}
          </span>
        );
      },
    },
    ...(canEdit && !isReadOnly
      ? [
          {
            key: 'actions',
            label: 'Actions',
            className: 'col-span-1',
            render: (_value: any, row: any) => (
              <div className="flex items-center space-x-2">
                <button type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditDocument(row);
                  }}
                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors duration-150"
                  title="Edit category">
                  <Edit className="h-4 w-4" />
                </button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="min-h-screen w-full bg-gray-50 dark:bg-gray-900 pb-6">
      <div className=" mx-auto">
        {/* Edit Category Modal */}
        <div style={{ zIndex: 9999 }}>
          <Modal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setDocumentToEdit(null);
            }}
            title="Edit Document Category"
            confirmText="Update"
            cancelText="Cancel"
            handleConfirm={() => {
              if (documentToEdit && documentToEdit.category) {
                handleUpdateDocument(
                  documentToEdit.documentId,
                  documentToEdit.category,
                );
              }
            }}>
            <div className="w-full">
              {documentToEdit && (
                <>
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      Document:{' '}
                      <span className="font-medium">
                        {documentToEdit.name || 'Unnamed Document'}
                      </span>
                    </p>
                  </div>

                  <FormGroup htmlFor="edit-category" label="Category">
                    <ReactSelect
                      id="edit-category"
                      isClearable={false}
                      options={DOCUMENT_CATEGORIES_OPTIONS}
                      placeholder="Select a category"
                      value={DOCUMENT_CATEGORIES_OPTIONS.find(
                        (option) => option.value === documentToEdit.category,
                      )}
                      onChange={(selectedOption: any) => {
                        setDocumentToEdit({
                          ...documentToEdit,
                          category:
                            selectedOption?.value || documentToEdit.category,
                        });
                      }}
                    />
                  </FormGroup>
                </>
              )}
            </div>
          </Modal>
        </div>

        {/* Detail Modal */}
        <div style={{ zIndex: 9999 }}>
          <Modal
            onClose={() => setShowDetailModal(false)}
            isOpen={showDetailModal}>
            <DocumentModal
              modalClose={setShowDetailModal}
              project={projects?.find(
                (project) => project.projectId === initialData?.task?.projectId,
              )}
              initialData={initialData}
            />
          </Modal>
        </div>
        {/* Manage Documents Modal */}
        <div style={{ zIndex: 9999 }}>
          <ProjectDocumentsModal
            category={category}
            setCategory={setCategory}
            documents={documents || []}
            handleDownload={handleDownload}
            isOpen={showManageDocumentsModal}
            handleFileChange={handleFileChange}
            deleteDocument={handleDeleteDocument}
            onClose={() => setShowManageDocumentsModal(false)}
          />
        </div>
        {/* Filter Bar */}
        <FilterBar
          documents={documents}
          filterText={filterText}
          projects={projects || []}
          showActions={showActions}
          projectId={projectId || null}
          filterCategory={filterCategory}
          onFilterTextChange={setFilterText}
          onClearFilters={handleClearFilters}
          selectedDocuments={selectedDocuments}
          selectedProjectId={selectedProjectId}
          onProjectChange={setSelectedProjectId}
          onDocumentsChange={setSelectedDocuments}
          onFilterCategoryChange={setFilterCategory}
          handleOpenManageDocuments={handleOpenManageDocuments}
          isReadOnly={isReadOnly}
        />

        <div className="pb-16">
          <DataTable
            canEdit={canEdit}
            data={documents}
            columns={columns}
            loading={loading}
            pagination={pagination}
            onRowClick={handleDetail}
            onPageChange={handlePageChange}
            emptyMessage="No documents found. Try adjusting your filters."
          />
        </div>
      </div>
    </div>
  );
};
